import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { MessageSquare, Users, TrendingUp, AlertTriangle } from 'lucide-react';
import { QuickMetrics } from '../../services/analyticsApi';

interface QuickMetricsCardProps {
  metrics: QuickMetrics;
}

export const QuickMetricsCard: React.FC<QuickMetricsCardProps> = ({ metrics }) => {
  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'negative':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'neutral':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getEmotionEmoji = (emotion?: string) => {
    const emotionMap: Record<string, string> = {
      joy: '😊',
      sadness: '😢',
      anger: '😠',
      fear: '😰',
      surprise: '😮',
      disgust: '🤢',
      neutral: '😐'
    };
    return emotionMap[emotion?.toLowerCase() || 'neutral'] || '🤔';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Quick Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Message Count */}
          <div className="flex flex-col space-y-2">
            <div className="flex items-center gap-2 text-gray-600">
              <MessageSquare className="h-4 w-4" />
              <span className="text-sm">Messages</span>
            </div>
            <span className="text-2xl font-bold">{metrics.message_count}</span>
          </div>

          {/* Participant Count */}
          <div className="flex flex-col space-y-2">
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="h-4 w-4" />
              <span className="text-sm">Participants</span>
            </div>
            <span className="text-2xl font-bold">{metrics.participant_count}</span>
          </div>

          {/* Primary Emotion */}
          {metrics.primary_emotion && (
            <div className="flex flex-col space-y-2">
              <span className="text-sm text-gray-600">Primary Emotion</span>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getEmotionEmoji(metrics.primary_emotion)}</span>
                <span className="text-lg font-semibold capitalize">{metrics.primary_emotion}</span>
              </div>
            </div>
          )}

          {/* Sentiment */}
          {metrics.sentiment_simple && (
            <div className="flex flex-col space-y-2">
              <span className="text-sm text-gray-600">Sentiment</span>
              <Badge className={getSentimentColor(metrics.sentiment_simple)}>
                {metrics.sentiment_simple}
              </Badge>
            </div>
          )}
        </div>

        {/* Attention Required Alert */}
        {metrics.requires_attention && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Attention Required</p>
              <p className="text-xs text-amber-700 mt-1">
                This conversation may require follow-up or intervention
              </p>
            </div>
          </div>
        )}

        {/* Deep Analysis Available */}
        {metrics.has_deep_analysis && (
          <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            Deep analysis available - switch to "Deep Analysis" tab for detailed insights
          </div>
        )}
      </CardContent>
    </Card>
  );
};

